import type {
  GridBackendAttributeFilter,
  GridBackendAttributeFilterOperator,
  GridBackendAttributeFilterType,
  ColumnFilterMap
} from '../d-data-grid/column-filter.types';
import { buildAttributeFilters } from '../d-data-grid/column-filter.utils';
import type { DataGridColumn } from '../d-data-grid/data-grid.types';

/**
 * HotChocolate `where`-clause operator → `AttributeFilterInput` operator.
 *
 * `in` / `nin` are intentionally omitted — `AttributeFilterInput` has no
 * matching operator. The sanitiser surfaces those as `unsupportedAliases`
 * so the caller can log a warning instead of silently corrupting the query.
 */
export const WHERE_TO_ATTRIBUTE_OPERATOR: ReadonlyMap<string, GridBackendAttributeFilterOperator> = new Map<
  string,
  GridBackendAttributeFilterOperator
>([
  ['eq', 'EQ'],
  ['neq', 'NEQ'],
  ['gt', 'GT'],
  ['gte', 'GTE'],
  ['lt', 'LT'],
  ['lte', 'LTE'],
  ['contains', 'CONTAINS'],
  ['ncontains', 'NOT_CONTAINS'],
  ['startsWith', 'STARTS_WITH'],
  ['nstartsWith', 'NOT_STARTS_WITH'],
  ['endsWith', 'ENDS_WITH'],
  ['nendsWith', 'NOT_ENDS_WITH']
]);

/**
 * Builds an IS_NULL / IS_NOT_NULL filter entry.
 *
 * IS_NULL / IS_NOT_NULL must not include a cast `type` — backends often reject
 * DECIMAL null checks on JSONB attributes.
 */
export function buildNullOperatorEntry(
  name: string,
  operator: 'IS_NULL' | 'IS_NOT_NULL'
): GridBackendAttributeFilter {
  return { name, operator, value: '' };
}

/**
 * Resolves the backend sort/filter type hint for a given column field.
 *
 * @returns `'DECIMAL'` for numeric, `'DATE'` for date columns; `undefined` for text.
 */
export function resolveColumnType(
  field: string,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>
): GridBackendAttributeFilterType | undefined {
  let col: DataGridColumn | undefined = sharedColumns.find((c: DataGridColumn): boolean => c.field === field);

  if (col === undefined) {
    for (const columns of Object.values(discriminatorColumns)) {
      col = columns.find((c: DataGridColumn): boolean => c.field === field);
      if (col !== undefined) {
        break;
      }
    }
  }

  if (col === undefined) {
    return undefined;
  }

  switch (col.type) {
    case 'numeric':
      return 'DECIMAL';
    case 'date':
      return 'DATE';
    default:
      return undefined;
  }
}

/**
 * Translates a single HotChocolate-style filter clause (e.g.
 * `{ eq: 1985 }`, `{ gte: 2000, lte: 2010 }`, `{ contains: 'steel' }`) into
 * one or more `GridBackendAttributeFilter` items targeting the given canonical alias.
 *
 * Returns an empty array when no operator in the clause can be mapped
 * (e.g. `{ eq: null }`, `{ in: [...] }`); the caller treats that as
 * "unsupported" and logs a warning rather than silently corrupting the payload.
 */
export function translateWhereClauseToAttributeFilter(
  field: string,
  clause: unknown,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>
): Array<GridBackendAttributeFilter> {
  if (typeof clause !== 'object' || clause === null) {
    return new Array<GridBackendAttributeFilter>();
  }
  const typeHint: GridBackendAttributeFilterType =
    resolveColumnType(field, sharedColumns, discriminatorColumns) ?? 'TEXT';
  const entries: Array<GridBackendAttributeFilter> = new Array<GridBackendAttributeFilter>();
  for (const [op, value] of Object.entries(clause as Record<string, unknown>)) {
    if (value === null) {
      if (op === 'eq') {
        entries.push(buildNullOperatorEntry(field, 'IS_NULL'));
      } else if (op === 'neq') {
        entries.push(buildNullOperatorEntry(field, 'IS_NOT_NULL'));
      }
      continue;
    }
    if (value === undefined) {
      continue;
    }
    const backendOp: GridBackendAttributeFilterOperator | undefined = WHERE_TO_ATTRIBUTE_OPERATOR.get(op);
    if (backendOp === undefined) {
      continue;
    }
    entries.push({ name: field, operator: backendOp, value: String(value), type: typeHint });
  }
  return entries;
}

/**
 * Splits a `where` payload into a sanitised `ElementFilterInput`-safe object
 * plus any extra `attributeFilter` entries derived from canonical-alias keys
 * that were stripped from `where`.
 *
 * Canonical-alias keys (e.g. `yearBuilt`) are not valid `ElementFilterInput`
 * fields — they live inside the JSONB `attributes` blob. Any clause the
 * sanitiser cannot translate (e.g. `{ eq: null }`, `{ in: [...] }`) is
 * dropped and the alias name is returned in `unsupportedAliases` so the
 * caller can log it.
 */
export function splitWhereByCanonicalAlias(
  where: Record<string, unknown>,
  remappedFields: ReadonlySet<string>,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>
): {
  where: Record<string, unknown>;
  extraAttributeFilter: Array<GridBackendAttributeFilter>;
  unsupportedAliases: Array<string>;
} {
  if (remappedFields.size === 0) {
    return { where, extraAttributeFilter: [], unsupportedAliases: [] };
  }

  // Short-circuit when the where payload has no alias keys to strip.
  let hasAliasKey: boolean = false;
  for (const key in where) {
    if (remappedFields.has(key)) {
      hasAliasKey = true;
      break;
    }
  }
  if (!hasAliasKey) {
    return { where, extraAttributeFilter: [], unsupportedAliases: [] };
  }

  const sanitised: Record<string, unknown> = {};
  const extras: Array<GridBackendAttributeFilter> = [];
  const unsupported: Array<string> = [];

  for (const [key, value] of Object.entries(where)) {
    if (!remappedFields.has(key)) {
      sanitised[key] = value;
      continue;
    }
    const translated: Array<GridBackendAttributeFilter> = translateWhereClauseToAttributeFilter(
      key,
      value,
      sharedColumns,
      discriminatorColumns
    );
    if (translated.length === 0) {
      unsupported.push(key);
      continue;
    }
    extras.push(...translated);
  }

  return { where: sanitised, extraAttributeFilter: extras, unsupportedAliases: unsupported };
}

/**
 * Builds the full `attributeFilter` array from active column filters,
 * routing canonical aliases through `buildAttributeFilters`.
 */
export function buildCurrentAttributeFilters(
  filters: ColumnFilterMap,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>,
  remappedFields: ReadonlySet<string>
): Array<GridBackendAttributeFilter> {
  if (filters.size === 0) {
    return new Array<GridBackendAttributeFilter>();
  }
  return buildAttributeFilters(filters, sharedColumns, discriminatorColumns, remappedFields);
}

/**
 * Merges active column `attributeFilter` entries with optional per-request
 * extras (e.g. ancestor alias scopes from sub-group discovery).
 *
 * @returns `undefined` when there are no filters at all (avoids sending an
 *   empty `attributeFilter` array to the backend).
 */
export function mergeAttributeFilters(
  filters: ColumnFilterMap,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>,
  remappedFields: ReadonlySet<string>,
  extras: ReadonlyArray<GridBackendAttributeFilter> = []
): Array<GridBackendAttributeFilter> | undefined {
  const columnFilters: Array<GridBackendAttributeFilter> = buildCurrentAttributeFilters(
    filters,
    sharedColumns,
    discriminatorColumns,
    remappedFields
  );
  const merged: Array<GridBackendAttributeFilter> =
    extras.length === 0 ? columnFilters : [...columnFilters, ...extras];
  return merged.length > 0 ? merged : undefined;
}

/**
 * Returns `true` when any of the given `attributeFilter` entries targets a
 * canonical alias with an IS_NULL / IS_NOT_NULL operator, indicating a
 * null-bucket scan may be needed.
 */
export function hasCanonicalNullAliasFilter(
  filters: ReadonlyArray<GridBackendAttributeFilter>,
  remappedFields: ReadonlySet<string>
): boolean {
  return filters.some(
    (entry: GridBackendAttributeFilter): boolean =>
      remappedFields.has(entry.name) && (entry.operator === 'IS_NULL' || entry.operator === 'IS_NOT_NULL')
  );
}
